import type { ReactNode } from 'react';
import { BookOpen, FileText, LifeBuoy, ArrowUpRight } from 'lucide-react';
import { DiscordIcon, WhopIcon, YouTubeIcon, InevitradeMark } from '../shared/BrandIcons';
import { ICE, TEAL, ACCENT, FIELD, PX_FONT, TITLE_FONT } from './theme';

/* Home Base resources: every community destination as an even grid of square tiles.
   Fill in `url` as links are confirmed; tiles without one show as "link coming". */

export interface Resource { key: string; name: string; blurb: string; icon: ReactNode; url?: string }

export const RESOURCES: Resource[] = [
  { key: 'discord', name: 'Discord', blurb: 'The community', icon: <DiscordIcon />, url: 'https://discord.gg/inevitrade' },
  { key: 'whop', name: 'Whop', blurb: 'Membership & billing', icon: <WhopIcon />, url: 'https://whop.com/inevitrade-trading-academy/' },
  { key: 'course', name: 'Course', blurb: 'On Teachable', icon: <InevitradeMark size={30} />, url: 'https://inevitrade.teachable.com/courses/inevitrade-crypto-accelerator' },
  { key: 'playbooks', name: 'Playbooks', blurb: 'VCL, SMOG and TC1 rules', icon: <BookOpen size={34} /> },
  // Open to all or Inner Circle only: still to decide (the rotation tooling itself stays Inner Circle)
  { key: 'marathon', name: 'Marathon PDF', blurb: 'Prop firm scaling, 3 to 25', icon: <FileText size={34} />, url: '/docs/marathon-method.pdf' },
  { key: 'youtube', name: 'YouTube', blurb: 'Craig Percoco', icon: <YouTubeIcon />, url: 'https://www.youtube.com/@craig_percoco' },
  { key: 'support', name: 'Support', blurb: 'Help center', icon: <LifeBuoy size={34} />, url: 'https://help.inevitrade.com/en/' },
];

function Tile({ r }: { r: Resource }) {
  const live = Boolean(r.url);
  const body = (
    <>
      {live && <ArrowUpRight size={14} style={{ position: 'absolute', top: 10, right: 10, color: TEAL }} />}
      <span className="pal-res-icon" style={{ color: live ? ACCENT : TEAL }}>{r.icon}</span>
      <span style={{ fontFamily: TITLE_FONT, fontWeight: 800, fontSize: 16, color: ICE, textAlign: 'center' }}>{r.name}</span>
      <span style={{ fontFamily: PX_FONT, fontSize: 11, lineHeight: 1.4, color: TEAL, textAlign: 'center' }}>{live ? r.blurb : 'Link coming'}</span>
    </>
  );
  const style = {
    position: 'relative' as const, aspectRatio: '1 / 1', flex: '0 0 var(--tile-w)', minWidth: 0, display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center',
    gap: 10, padding: 14, textDecoration: 'none', background: FIELD, borderRadius: 'var(--pal-radius, 0)',
    boxShadow: 'inset 0 0 0 1px var(--pal-rim)', opacity: live ? 1 : 0.7,
  };
  return live
    ? <a className="pal-res-tile" href={r.url} target="_blank" rel="noreferrer" style={style}>{body}</a>
    : <div className="pal-res-tile" aria-disabled style={style}>{body}</div>;
}

export function ResourceGrid() {
  // One even row on wide screens; when it wraps, the last row stays centred so it still looks balanced
  return (
    <div className="pal-res-grid" style={{ ['--n' as string]: RESOURCES.length, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 14, paddingTop: 18 }}>
      {RESOURCES.map(r => <Tile key={r.key} r={r} />)}
    </div>
  );
}
